import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Textarea } from '@rudi/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Flag, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useFlagIdentity, useIdentityEntities, useSetFindingStatus } from '../api/hooks';
import type {
  IdentityCandidateRecord,
  IdentityFinding,
  IdentityFindingStatus,
  IdentityLevel,
  IdentityLink,
  IdentityListParams,
  IdentityMention,
  IdentitySort,
} from '../api/types';

// Shared parts of the Medical data quality pages: the Medical Repository's
// Organisations, Catalog Sites, Departments and Investigators, reviewed through Rudi.

export const LEVEL_NAME: Record<IdentityLevel, { one: string; many: string }> = {
  organisation: { one: 'Organisation', many: 'Organisations' },
  site: { one: 'Catalog Site', many: 'Catalog Sites' },
  department: { one: 'Department', many: 'Departments' },
  person: { one: 'Investigator', many: 'Investigators' },
};

export const PAGE_SIZE = 50;

export const control =
  'h-10 w-full rounded-3xl border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const SECTIONS = [
  { to: '/data-quality', label: 'Overview', exact: true },
  { to: '/data-quality/organisations', label: 'Organisations', exact: false },
  { to: '/data-quality/investigators', label: 'Investigators', exact: false },
  { to: '/data-quality/review', label: 'Review queue', exact: false },
  { to: '/data-quality/findings', label: 'Findings', exact: false },
] as const;

export function DataQualityNav() {
  const tab = 'rounded-3xl border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent';
  return (
    <nav aria-label="Medical data quality" className="mb-5 flex flex-wrap gap-2">
      {SECTIONS.map(section => (
        <Link
          key={section.to}
          to={section.to}
          className={tab}
          activeOptions={{ exact: section.exact }}
          activeProps={{ className: `${tab} bg-accent font-medium text-accent-foreground` }}
        >
          {section.label}
        </Link>
      ))}
    </nav>
  );
}

const DETAIL_ROUTE = {
  organisation: '/data-quality/organisations/$entityId',
  site: '/data-quality/catalog-sites/$entityId',
  department: '/data-quality/departments/$entityId',
  person: '/data-quality/investigators/$entityId',
} as const;

export function EntityLink({
  kind,
  id,
  children,
}: {
  kind: IdentityLevel;
  id: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={DETAIL_ROUTE[kind]}
      params={{ entityId: id }}
      className="underline-offset-4 hover:underline"
    >
      {children}
    </Link>
  );
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function detailOf(error: unknown): string | undefined {
  const data = (error as { response?: { data?: { detail?: unknown; error?: unknown } } })?.response
    ?.data;
  const detail = data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map(item => (item as { msg?: string })?.msg)
      .filter(Boolean)
      .join('; ');
  return typeof data?.error === 'string' ? data.error : undefined;
}

/**
 * The Medical Repository answers 404 for an entity merged or removed since the
 * page loaded, and 409 for a decision someone already made or that conflicts.
 */
export function identityErrorText(error: unknown, fallback: string): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const detail = detailOf(error);
  if (status === 409)
    return `Already decided or in conflict: ${detail ?? 'another decision came first'}. Reload to see the current state.`;
  if (status === 404)
    return 'Not found. It was merged elsewhere or removed; reload to see the current state.';
  if (status === 503) return 'The Medical Repository is unavailable. Try again in a few minutes.';
  if (status === 403) return 'Rudi did not allow this. Only super admins review medical data.';
  if (detail) return capitalise(detail);
  return error instanceof Error ? error.message : fallback;
}

/** For QueryState, which shows an Error's message. */
export function loadError(error: unknown): Error | null {
  if (!error) return null;
  const status = (error as { response?: { status?: number } })?.response?.status;
  // A list route that is missing means Rudi is older than this page.
  if (status === 404 || status === 405 || status === 501)
    return new Error('This Rudi release does not offer medical data review yet.');
  return new Error(identityErrorText(error, 'Could not load medical data'));
}

export function humanise(rule: string | null | undefined): string {
  return rule ? rule.replaceAll('_', ' ') : 'none';
}

export function scoreText(score: number | null | undefined): string {
  return score === null || score === undefined ? 'no score' : score.toFixed(2);
}

export function placeText(parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(', ');
}

/** "12203 Berlin", the German order of postal code and city. */
export function postalCity(postalCode: string | null, city: string | null): string {
  return [postalCode, city].filter(Boolean).join(' ');
}

/** How a Mention was linked: identifier, auto or steward, with the rule and score. */
export function LinkEvidence({ link }: { link: IdentityLink | undefined }) {
  if (!link?.id) return <span className="text-muted-foreground">Not linked</span>;
  return (
    <span className="whitespace-nowrap">
      <Badge variant={link.method === 'steward' ? 'default' : 'secondary'}>{link.method}</Badge>{' '}
      <span className="text-xs text-muted-foreground">
        {humanise(link.rule)} · {scoreText(link.score)}
      </span>
    </span>
  );
}

/** The raw registry text of a Mention's site. */
export function MentionPlace({ mention }: { mention: IdentityMention }) {
  return (
    <div>
      <div className="font-medium">{mention.organisation_name ?? 'No organisation name'}</div>
      {mention.department && <div className="text-xs">{mention.department}</div>}
      <div className="text-xs text-muted-foreground">
        {placeText([mention.address, postalCity(mention.postal_code, mention.city)]) ||
          'No address'}
      </div>
    </div>
  );
}

export function Pager({
  total,
  limit,
  offset,
  onChange,
}: {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
}) {
  if (total === 0) return null;
  const end = Math.min(offset + limit, total);
  return (
    <div className="mt-3 flex items-center justify-between gap-3 text-sm text-muted-foreground">
      <span role="status">
        {offset + 1}–{end} of {total}
      </span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={end >= total}
          onClick={() => onChange(offset + limit)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

const SORTS: { value: IdentitySort; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'studies', label: 'Most studies' },
  { value: 'mentions', label: 'Most Mentions' },
  { value: 'flags', label: 'Most open flags' },
  { value: 'updated', label: 'Recently changed' },
];

/** Search, sort and flag filters of an entity list. Every change starts at the first page. */
export function EntityFilters({
  level,
  params,
  onChange,
  withCity,
}: {
  level: IdentityLevel;
  params: IdentityListParams;
  onChange: (params: IdentityListParams) => void;
  withCity?: boolean;
}) {
  const [q, setQ] = useState(params.q ?? '');
  const [city, setCity] = useState(params.city ?? '');
  const apply = (next: IdentityListParams) => onChange({ ...params, ...next, offset: 0 });
  return (
    <form
      className="mb-4 flex flex-wrap items-end gap-3 rounded-3xl border bg-card p-4"
      onSubmit={event => {
        event.preventDefault();
        apply({ q: q.trim() || undefined, city: city.trim() || undefined });
      }}
    >
      <Labelled label="Search">
        <Input
          aria-label={`Search ${LEVEL_NAME[level].many}`}
          placeholder={level === 'person' ? 'Name or slug' : 'Name'}
          value={q}
          onChange={event => setQ(event.target.value)}
        />
      </Labelled>
      {withCity && (
        <Labelled label="City">
          <Input aria-label="City" value={city} onChange={event => setCity(event.target.value)} />
        </Labelled>
      )}
      <Labelled label="Sort by">
        <select
          aria-label="Sort by"
          className={control}
          value={params.sort ?? 'name'}
          onChange={event => apply({ sort: event.target.value as IdentitySort })}
        >
          {SORTS.map(sort => (
            <option key={sort.value} value={sort.value}>
              {sort.label}
            </option>
          ))}
        </select>
      </Labelled>
      <Labelled label="Flags">
        <select
          aria-label="Flags"
          className={control}
          value={params.flagged === undefined ? '' : String(params.flagged)}
          onChange={event =>
            apply({
              flagged: event.target.value === '' ? undefined : event.target.value === 'true',
            })
          }
        >
          <option value="">All</option>
          <option value="true">With open flags</option>
          <option value="false">Without open flags</option>
        </select>
      </Labelled>
      <Button type="submit">
        <Search className="h-4 w-4" />
        Search
      </Button>
    </form>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="mb-6 space-y-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      {children}
    </section>
  );
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** An entity page's open Link Candidates and Findings. */
export function EntityReview({
  candidates,
  findings,
}: {
  candidates: IdentityCandidateRecord[];
  findings: IdentityFinding[];
}) {
  return (
    <>
      {candidates.length > 0 && (
        <Section title={`Open Link Candidates (${candidates.length})`}>
          <ul className="space-y-1 text-sm">
            {candidates.map(candidate => (
              <li key={candidate.id}>
                {humanise(candidate.rule)} · score {scoreText(candidate.score)}
              </li>
            ))}
          </ul>
          <Link to="/data-quality/review" className="mt-2 inline-block text-sm underline">
            Decide them in the review queue
          </Link>
        </Section>
      )}
      <Section title={`Findings (${findings.length})`}>
        {findings.length ? (
          <FindingsTable findings={findings} showEntity={false} />
        ) : (
          <p className="text-sm text-muted-foreground">No Findings.</p>
        )}
      </Section>
    </>
  );
}

export function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex min-w-36 flex-1 flex-col gap-1 text-xs text-muted-foreground">
      {label}
      {children}
    </label>
  );
}

export function ReasonField({
  value,
  onChange,
  label = 'Reason',
  placeholder = 'Optional reason for the audit trail',
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
}) {
  return (
    <Textarea
      aria-label={label}
      placeholder={placeholder}
      value={value}
      maxLength={2000}
      rows={2}
      onChange={event => onChange(event.target.value)}
    />
  );
}

/**
 * Pick another entity of one level by name, then confirm a decision about it.
 * `allowNone` adds a choice for "no entity", such as removing a parent.
 */
export function EntityPickerDialog({
  open,
  onClose,
  level,
  title,
  description,
  confirmLabel,
  excludeIds = [],
  filter,
  pinned = [],
  allowNone,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  level: IdentityLevel;
  title: string;
  description: string;
  confirmLabel: string;
  excludeIds?: string[];
  /** Narrows the choices, such as to the Catalog Sites of one Organisation. */
  filter?: IdentityListParams;
  /** Choices shown first, such as the Catalog Site at a Mention's address. */
  pinned?: { id: string; label: string; note: string }[];
  allowNone?: string;
  onConfirm: (targetId: string | null, reason: string) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<string | null | undefined>(undefined);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const results = useIdentityEntities(level, { ...filter, q: query || undefined, limit: 25 }, open);
  const hidden = [...excludeIds, ...pinned.map(item => item.id)];
  const options = (results.data?.items ?? []).filter(item => !hidden.includes(item.id));

  const reset = () => {
    setDraft('');
    setQuery('');
    setChosen(undefined);
    setReason('');
    setError(null);
    onClose();
  };
  // Closing while a decision is in flight would hide its result.
  const close = () => !busy && reset();

  const confirm = async () => {
    if (chosen === undefined) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(chosen, reason);
      setBusy(false);
      reset();
    } catch (failure) {
      setBusy(false);
      setError(identityErrorText(failure, 'The decision failed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => !next && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form
          className="flex gap-2"
          onSubmit={event => {
            event.preventDefault();
            setQuery(draft.trim());
          }}
        >
          <Input
            aria-label={`Search ${LEVEL_NAME[level].many}`}
            placeholder={`Search ${LEVEL_NAME[level].many.toLowerCase()} by name`}
            value={draft}
            onChange={event => setDraft(event.target.value)}
          />
          <Button type="submit" variant="outline" aria-label="Search">
            <Search className="h-4 w-4" />
          </Button>
        </form>
        <div
          role="radiogroup"
          aria-label={`Choose ${LEVEL_NAME[level].one}`}
          className="max-h-64 space-y-1 overflow-y-auto"
        >
          {allowNone && (
            <PickerOption checked={chosen === null} onSelect={() => setChosen(null)}>
              {allowNone}
            </PickerOption>
          )}
          {pinned.map(item => (
            <PickerOption
              key={item.id}
              checked={chosen === item.id}
              onSelect={() => setChosen(item.id)}
            >
              <span className="font-medium">{item.label}</span>
              <span className="ml-2 text-xs text-muted-foreground">{item.note}</span>
            </PickerOption>
          ))}
          {results.isLoading && <p className="text-sm text-muted-foreground">Searching…</p>}
          {results.error ? (
            <p role="alert" className="text-sm text-destructive">
              {identityErrorText(results.error, 'Search failed')}
            </p>
          ) : null}
          {!results.isLoading && !results.error && options.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {pinned.length ? 'No other matches.' : 'No matches.'}
            </p>
          )}
          {options.map(option => (
            <PickerOption
              key={option.id}
              checked={chosen === option.id}
              onSelect={() => setChosen(option.id)}
            >
              <span className="font-medium">{option.display_name}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {pickerContext(option)} · {option.study_count} studies
              </span>
            </PickerOption>
          ))}
        </div>
        <ReasonField value={reason} onChange={setReason} />
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={busy || chosen === undefined} onClick={() => void confirm()}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function pickerContext(option: {
  kind: IdentityLevel;
  city?: string | null;
  organisation_name?: string | null;
  site_name?: string | null;
  organisations?: string[];
}): string {
  if (option.kind === 'site') return placeText([option.organisation_name, option.city]);
  if (option.kind === 'department') return option.site_name ?? '';
  if (option.kind === 'person') return (option.organisations ?? []).join(', ');
  return option.city ?? '';
}

function PickerOption({
  checked,
  onSelect,
  children,
}: {
  checked: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      className={`block w-full rounded-3xl border px-3 py-2 text-left text-sm hover:bg-accent ${
        checked ? 'border-primary bg-accent' : ''
      }`}
    >
      {children}
    </button>
  );
}

/** A dialog with a note field, for flags and finding decisions. */
export function NoteDialog({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  noteRequired,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  confirmLabel: string;
  noteRequired?: boolean;
  onConfirm: (note: string) => Promise<unknown>;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reset = () => {
    setNote('');
    setError(null);
    onClose();
  };
  const close = () => !busy && reset();
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(note.trim());
      setBusy(false);
      reset();
    } catch (failure) {
      setBusy(false);
      setError(identityErrorText(failure, 'The request failed'));
    }
  };
  return (
    <Dialog open={open} onOpenChange={next => !next && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <ReasonField
          label="Note"
          placeholder={noteRequired ? 'What is wrong?' : 'Optional note'}
          value={note}
          onChange={setNote}
        />
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={busy || (noteRequired && !note.trim())} onClick={() => void confirm()}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Raise a reviewer Finding on an entity, such as "campus closed". */
export function FlagButton({
  level,
  id,
  name,
  size = 'sm',
}: {
  level: IdentityLevel;
  id: string;
  name: string;
  size?: 'sm' | 'xs';
}) {
  const [open, setOpen] = useState(false);
  const flag = useFlagIdentity();
  return (
    <>
      <Button
        variant="outline"
        size={size}
        aria-label={`Flag ${name}`}
        onClick={() => setOpen(true)}
      >
        <Flag className="h-4 w-4" />
        Flag
      </Button>
      <NoteDialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Flag ${name}`}
        description={`Note what is wrong with this ${LEVEL_NAME[level].one}. The flag stays open in Findings until someone resolves it.`}
        confirmLabel="Flag"
        noteRequired
        onConfirm={async note => {
          await flag.mutateAsync({ level, id, note });
          toast.success('Flag raised');
        }}
      />
    </>
  );
}

export const FINDING_KIND: Record<string, string> = {
  person_many_organisations: 'Investigator at many Organisations',
  large_person_cluster: 'Large Investigator cluster',
  shared_email: 'Email used by several names',
  organisation_only: 'Mentions without a Catalog Site',
  postal_code_city: 'Postal code outside the city',
  shared_address: 'Organisations sharing an address',
  flag: 'Reviewer flag',
};

export function findingKindText(kind: string): string {
  return FINDING_KIND[kind] ?? capitalise(humanise(kind));
}

const SOURCE_TEXT: Record<string, string> = { rule: 'Rule', reviewer: 'Reviewer', ai: 'AI' };

function FindingStatusBadge({ status }: { status: IdentityFindingStatus }) {
  if (status === 'open') return <Badge variant="destructive">open</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

function FindingDetails({ finding }: { finding: IdentityFinding }) {
  const details = finding.details ?? {};
  const organisations = Array.isArray(details.organisation_ids)
    ? (details.organisation_ids as string[])
    : [];
  const rest = Object.entries(details).filter(
    ([key]) => !['organisation_ids', 'resolution_note', 'service', 'suggestion_id'].includes(key)
  );
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      {organisations.length > 0 && (
        <div>
          Organisations:{' '}
          {organisations.map((id, index) => (
            <span key={id}>
              {index > 0 && ', '}
              <EntityLink kind="organisation" id={id}>
                {id.slice(0, 8)}
              </EntityLink>
            </span>
          ))}
        </div>
      )}
      {rest.map(([key, value]) => (
        <div key={key}>
          {humanise(key)}: {Array.isArray(value) ? value.join(', ') : String(value)}
        </div>
      ))}
      {typeof details.resolution_note === 'string' && (
        <div>Resolution note: {details.resolution_note}</div>
      )}
    </div>
  );
}

type FindingAction = { status: IdentityFindingStatus; label: string; done: string };

const FINDING_ACTIONS: Record<IdentityFindingStatus, FindingAction[]> = {
  open: [
    { status: 'resolved', label: 'Resolve', done: 'Finding resolved' },
    { status: 'dismissed', label: 'Dismiss', done: 'Finding dismissed' },
  ],
  resolved: [{ status: 'open', label: 'Reopen', done: 'Finding reopened' }],
  dismissed: [{ status: 'open', label: 'Reopen', done: 'Finding reopened' }],
};

function FindingActions({ finding }: { finding: IdentityFinding }) {
  const [action, setAction] = useState<FindingAction | null>(null);
  const setStatus = useSetFindingStatus();
  const title = findingKindText(finding.kind);
  return (
    <div className="flex justify-end gap-2">
      {FINDING_ACTIONS[finding.status].map(item => (
        <Button
          key={item.status}
          size="sm"
          variant={item.status === 'resolved' ? 'default' : 'outline'}
          aria-label={`${item.label} ${title}`}
          onClick={() => setAction(item)}
        >
          {item.label}
        </Button>
      ))}
      <NoteDialog
        open={action !== null}
        onClose={() => setAction(null)}
        title={`${action?.label ?? ''} finding`}
        description={finding.note}
        confirmLabel={action?.label ?? ''}
        onConfirm={async note => {
          if (!action) return;
          await setStatus.mutateAsync({ id: finding.id, status: action.status, note });
          toast.success(action.done);
        }}
      />
    </div>
  );
}

/** Findings with links to the entities involved, and resolve, dismiss or reopen with a note. */
export function FindingsTable({
  findings,
  showEntity = true,
}: {
  findings: IdentityFinding[];
  showEntity?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-3xl border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Finding</TableHead>
            {showEntity && <TableHead>Concerns</TableHead>}
            <TableHead>Source</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {findings.map(finding => (
            <TableRow key={finding.id}>
              <TableCell className="min-w-[18rem]">
                <div className="font-medium">{findingKindText(finding.kind)}</div>
                <div className="text-sm">{finding.note}</div>
                <FindingDetails finding={finding} />
              </TableCell>
              {showEntity && (
                <TableCell>
                  {finding.entity_kind && finding.entity_id ? (
                    <EntityLink kind={finding.entity_kind} id={finding.entity_id}>
                      {LEVEL_NAME[finding.entity_kind].one} {finding.entity_id.slice(0, 8)}
                    </EntityLink>
                  ) : (
                    <span className="text-muted-foreground">No single entity</span>
                  )}
                </TableCell>
              )}
              <TableCell>
                <Badge variant="outline">{SOURCE_TEXT[finding.source] ?? finding.source}</Badge>
                <div className="mt-1 text-xs text-muted-foreground">{finding.created_by}</div>
              </TableCell>
              <TableCell>
                <FindingStatusBadge status={finding.status} />
                {finding.resolved_by && (
                  <div className="mt-1 text-xs text-muted-foreground">by {finding.resolved_by}</div>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {new Date(finding.updated_at).toLocaleString()}
              </TableCell>
              <TableCell>
                <FindingActions finding={finding} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
